import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useMindMap } from 'compositions/mindmap'
import { changeDpiDataUrl } from 'changedpi'
import * as ArrayBufferConverter from 'base64-arraybuffer'
import { SVGToOtherType } from 'client/library/workbook-next/libs/image'
import { createSVGUrl } from 'app/services/action/export-as/export-svg'
import { MindMap } from 'client/library/workbook-next/types'
import { PDFDocument } from 'pdf-lib'

const EXPORT_SCALE = 2

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
    reader.readAsDataURL(blob)
  })

async function exportCurrentSheetPng(sheetId: string) {
  const { getSheetViewer } = useMindMap()
  const sheetViewer = getSheetViewer(sheetId)
  if (!sheetViewer) {
    throw new Error('Sheet viewer not found')
  }

  const { size, svg } = await sheetViewer.exportSVGElementWithSize({
    padding: { left: 50, right: 50, top: 50, bottom: 120 },
    opaqueBackground: true,
    disableEmbedFonts: false,
    useSplitText: true,
  })

  const svgBlob = await createSVGUrl(svg, size, { scale: EXPORT_SCALE, hasWatermark: true, isTransparent: false }, sheetId)
  const svgDataUrl = await blobToDataUrl(svgBlob)
  const base64PNG = await SVGToOtherType(svgDataUrl as `data:image/svg+xml,${string}`, EXPORT_SCALE, 'string', 'image/png')
  if (!base64PNG) {
    throw new Error('Failed to export PNG')
  }
  const imageWithDpi = changeDpiDataUrl(base64PNG, 144)
  const entity = imageWithDpi.replace(/^data:image\/png;base64,/, '')
  const buffer = ArrayBufferConverter.decode(entity)
  return new Blob([buffer], { type: 'image/png' })
}

async function exportCurrentSheetJpg(sheetId: string) {
  const { getSheetViewer } = useMindMap()
  const sheetViewer = getSheetViewer(sheetId)
  if (!sheetViewer) {
    throw new Error('Sheet viewer not found')
  }

  const { size, svg } = await sheetViewer.exportSVGElementWithSize({
    padding: { left: 50, right: 50, top: 50, bottom: 120 },
    opaqueBackground: true,
    disableEmbedFonts: false,
    useSplitText: true,
  })

  const svgBlob = await createSVGUrl(svg, size, { scale: EXPORT_SCALE, hasWatermark: true, isTransparent: false }, sheetId)
  const svgDataUrl = await blobToDataUrl(svgBlob)
  const base64JPG = await SVGToOtherType(svgDataUrl as `data:image/svg+xml,${string}`, EXPORT_SCALE, 'string', 'image/jpeg', 0.9)
  if (!base64JPG) {
    throw new Error('Failed to export JPG')
  }
  const entity = base64JPG.replace(/^data:image\/jpeg;base64,/, '')
  const buffer = ArrayBufferConverter.decode(entity)
  return new Blob([buffer], { type: 'image/jpeg' })
}

type PDFExportArgs = {
  isTransparent?: boolean
  scale?: number
}

export const pdfExportPromise = async (args: PDFExportArgs & { sheetId: string; workbookEditor: MindMap }) => {
  const { sheetId, workbookEditor } = args
  const sheetView = workbookEditor.getSheetViewer(sheetId)
  if (!sheetView) {
    return null
  }
  const disableEmbedFonts = true
  const scale = args.scale || 2
  const svgInfo = await sheetView.exportSVGElementWithSize({
    padding: { left: 50, right: 50, top: 50, bottom: 50 },
    opaqueBackground: !args.isTransparent,
    disableEmbedFonts,
  })

  if (!svgInfo?.svg || !svgInfo?.size) {
    throw new Error('Invalid export result')
  }

  const svgElement = svgInfo.svg
  svgElement.setAttribute('id', 'export-pdf-element')
  svgElement.style.position = 'absolute'
  svgElement.style.top = '0'
  svgElement.style.left = '50%'
  svgElement.style.transform = 'translateX(-50%)'
  svgElement.style.width = '100%'
  svgElement.style.height = '100%'

  const svgString = new XMLSerializer().serializeToString(svgElement)
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
  const base64PNG = await SVGToOtherType(svgDataUrl as `data:image/svg+xml,${string}`, scale, 'string', 'image/png')
  if (base64PNG === '') {
    throw new Error('Export image failed')
  }
  const imageFixDPI = changeDpiDataUrl(base64PNG, 144)
  const base64Data = imageFixDPI.replace(/^data:image\/png;base64,/, '')
  const binaryString = atob(base64Data)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const pdfDoc = await PDFDocument.create()
  const pngImage = await pdfDoc.embedPng(bytes)
  const page = pdfDoc.addPage([svgInfo.size.width, svgInfo.size.height])
  page.drawImage(pngImage, {
    x: 0,
    y: 0,
    width: svgInfo.size.width,
    height: svgInfo.size.height,
  })

  const pdfBytes = await pdfDoc.save()

  return pdfBytes
}

export const exportPngAction = async () => {
  const workspaceStore = useWorkspaceStore()
  const workbook = workspaceStore.workbook
  if (!workbook) {
    throw new Error('Workbook not found')
  }
  const sheetId = workbook.workbookEditor.activeSheetId.value
  if (!sheetId) {
    throw new Error('Active sheet not found')
  }

  workspaceStore.setMapStatus('exporting')
  try {
    return await exportCurrentSheetPng(sheetId)
  } finally {
    workspaceStore.setMapStatus('loaded')
  }
}

export const exportJpgAction = async () => {
  const workspaceStore = useWorkspaceStore()
  const workbook = workspaceStore.workbook
  if (!workbook) {
    throw new Error('Workbook not found')
  }
  const sheetId = workbook.workbookEditor.activeSheetId.value
  if (!sheetId) {
    throw new Error('Active sheet not found')
  }

  workspaceStore.setMapStatus('exporting')
  try {
    return await exportCurrentSheetJpg(sheetId)
  } finally {
    workspaceStore.setMapStatus('loaded')
  }
}

export const exportPDFAction = async (args?: PDFExportArgs) => {
  const workspaceStore = useWorkspaceStore()
  const workbook = workspaceStore.workbook
  if (!workbook) {
    throw new Error('Workbook not found')
  }
  const sheetId = workbook.workbookEditor.activeSheetId.value
  if (!sheetId) {
    throw new Error('Active sheet not found')
  }

  workspaceStore.setMapStatus('exporting')
  try {
    const pdfBytes = await pdfExportPromise({ sheetId, workbookEditor: workbook.workbookEditor, ...args })
    if (pdfBytes) {
      // @ts-ignore
      return new Blob([pdfBytes], { type: 'application/pdf' })
    }
  } finally {
    workspaceStore.setMapStatus('loaded')
  }
}
